<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '900px'"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
		scrollable
	>
		<v-card
			class="userflow-core-selector-dialog"
			data-test="core-selector-dialog"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				Select Active Core
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<div class="content-grid sgnl-text-color-secondary sgnl-scrollbar">
				<ErrorEmptyState
					v-if="coresStore.userTierResolutionFailed"
					class="content-grid-error"
					compact
					title="Could not load Cores"
					message="Often this is a temporary network or connectivity issue."
					:retrying="retryingLoad || coresStore.loadingCores"
					@retry="retryLoad"
				/>
				<template v-else>
					<!-- Core list section -->
					<v-item-group
						data-test="core-selector-menu"
						class="content-grid-core-list"
					>
						<v-item
							v-for="core in selectableCoresList"
							:key="core.CoreID"
						>
							<v-card
								:class="[
									core.Disabled ? 'sgnl-text-color-disabled' : null,
									'sgnl-align-center',
									`userflow-core-dialog-${core.Name.toLowerCase()
										.replace(/\+/g, 'plus')
										.replace(/[^a-z0-9]/g, '-')}`,
								]"
								:style="[
									{ flexShrink: 0 },
									core.CoreID === selectedCore?.CoreID
										? { backgroundColor: 'var(--sgnl-default-secondary-background-mono)' }
										: {},
								]"
								@click="selectedCore = core"
								variant="outlined"
								rounded="md"
								class="sgnl-flex sgnl-justify-space-between sgnl-gap-8 sgnl-px-16 sgnl-py-12 sgnl-paragraph-s-regular"
								data-test="core-selector-menu-item"
								><span class="sgnl-text-truncate">{{ core.Name }}</span>
								<Locked16
									v-if="core.Disabled"
									class="sgnl-icon-color-error"
								/>

								<ErrorOutline16
									v-if="!core.Disabled && !satisfiesMinimumCore(core)"
									class="sgnl-icon-color-warning"
								/>
							</v-card>
						</v-item>
					</v-item-group>

					<!-- Divider -->
					<v-divider
						class="content-grid-grid-divider"
						:inset="$vuetify.display.mdAndUp"
						:vertical="$vuetify.display.mdAndUp"
					/>

					<!-- Core details section -->
					<div class="content-grid-core-details">
						<div class="sgnl-flex sgnl-flex-column sgnl-gap-4">
							<div class="sgnl-h6-medium sgnl-text-color-default">
								{{ selectedCore?.Name }}
							</div>

							<div class="inline-code sgnl-text-color-secondary sgnl-paragraph-s-regular">
								{{ selectedCore?.CoreID }}
							</div>
						</div>
						<v-fade-transition>
							<AlertMessage
								v-if="selectedCore && !satisfiesMinimumCore(selectedCore)"
								type="warning"
								class="core-warning-alert"
								>This core does not meet the recommended core specification.</AlertMessage
							>
						</v-fade-transition>
						<div class="core-details-spec-group">
							<v-table
								density="compact"
								:key="selectedCore?.CoreID"
							>
								<template v-slot:default>
									<thead>
										<tr>
											<th class="sgnl-text-left" />
											<th class="sgnl-text-left sgnl-text-color-default">Selected Core</th>
											<th
												v-if="minimumCore"
												class="sgnl-text-left sgnl-text-color-default"
											>
												<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
													<span class="sgnl-text-no-wrap">Recommended Core</span>
													<DocumentationIcon
														tooltip-text="What is this?"
														documentation-link="https://docs.signaloid.io/docs/platform/user-interface/repositories/use-signaloid-yaml"
													/>
												</div>
											</th>
										</tr>
									</thead>
									<tbody v-if="selectedCore != undefined">
										<template v-for="key in fieldsToDisplay">
											<tr
												v-if="Object.hasOwnProperty.call(selectedCore, key)"
												:key="key"
												:class="coreRequirementWarnings[key] ? 'sgnl-text-color-warning' : ''"
											>
												<td class="sgnl-paragraph-s-medium sgnl-text-color-default">
													{{ formatKeyString(key) }}
												</td>
												<td class="cell">
													{{ formatValueString(key, selectedCore[key]) }}
												</td>
												<td
													v-if="minimumCore"
													class="cell sgnl-text-left"
												>
													<span
														v-if="limitValuePrefix(key)"
														class="sgnl-caption-regular"
														:class="
															coreRequirementWarnings[key]
																? 'sgnl-text-color-warning'
																: ''
														"
														>≥</span
													>
													{{ formatValueString(key, minimumCore[key]) ?? "-" }}
												</td>
											</tr>
										</template>
									</tbody>
								</template>
							</v-table>
							<div>
								<span class="sgnl-paragraph-s-regular">
									{{ coreDescription }}
								</span>
							</div>
						</div>
					</div>
				</template>
			</div>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="closeDialog"
					>Cancel</v-btn
				>
				<v-btn
					v-if="selectedCore?.Disabled"
					data-test="core-selector-set-core-button"
					variant="flat"
					color="primary"
					to="/billing"
				>
					Upgrade for Access
				</v-btn>
				<v-btn
					v-else
					class="userflow-core-selector-confirm-button"
					data-test="core-selector-set-core-button"
					variant="flat"
					color="primary"
					type="submit"
					@click="setCore(selectedCore?.CoreID)"
				>
					Select Core
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import ErrorOutline16 from "@carbon/icons-vue/es/error--outline/16";
import Locked16 from "@carbon/icons-vue/es/locked/16";

// Stores
import { mapState } from "pinia";
import { useCoresStore } from "@/stores/cores";

import { coreDescriptionString, toStringWithByteMagnitude, toStringWithSIMagnitude } from "@/js/utilities";
import { Core, CoreRequirementMatch } from "@/types/api/cores";
import DocumentationIcon from "./Common/DocumentationIcon.vue";
import AlertMessage from "./Common/AlertMessage.vue";
import ErrorEmptyState from "./Common/ErrorEmptyState.vue";

type ComponentData = {
	//TODO: Change this to Core
	selectedCore: Core | undefined;
	retryingLoad: boolean;
};

export default defineComponent({
	name: "SelectCoreDialog",
	components: { DocumentationIcon, Close16, ErrorOutline16, Locked16, AlertMessage, ErrorEmptyState },
	props: {
		showDialog: { type: Boolean, default: false },
		minimumCore: { type: Object as PropType<Partial<Core> | undefined>, default: undefined },
		activeCore: { type: Object as PropType<Core | undefined>, default: undefined },
	},
	emits: ["select-core", "close-dialog", "update:showDialog", "set-core"],
	data: (): ComponentData => ({
		selectedCore: undefined,
		retryingLoad: false,
	}),
	setup() {
		const fieldsToDisplay = [
			// "CoreID",
			// "Name",
			"Class",
			"Precision",
			"MemorySize",
			"Microarchitecture",
			"CorrelationTracking",
		];
		const coresStore = useCoresStore();
		return {
			coresStore,
			fieldsToDisplay,
		};
	},
	methods: {
		closeDialog() {
			this.$emit("close-dialog");
		},
		async retryLoad(): Promise<void> {
			this.retryingLoad = true;
			try {
				await this.coresStore.getCoresFilteredCoresByUserTier();
			} finally {
				this.retryingLoad = false;
			}
		},
		setCore(coreID) {
			this.$emit("set-core", coreID);
		},
		limitValuePrefix(key) {
			switch (key) {
				case "MemorySize":
				case "Precision":
					return true;
				default:
					return false;
			}
		},
		formatValueString(key, value) {
			switch (key) {
				case "MemorySize":
					return toStringWithByteMagnitude(value);
				case "Precision":
					return toStringWithSIMagnitude(value, "");
				case "CorrelationTracking":
					return this.formatCorrelationString(value);
				default:
					return value;
			}
		},
		formatKeyString(key) {
			switch (key) {
				case "CoreID":
					return "Core ID";
				case "MemorySize":
					return "Memory Size";
				case "CorrelationTracking":
					return "Correlation Tracking";
				default:
					return key;
			}
		},
		formatCorrelationString(code) {
			return this.correlationTrackingMethodByCode(code)?.Name ?? "-";
		},
		satisfiesMinimumCore(core): boolean {
			if (!this.minimumCore) {
				return true;
			}
			if (this.coresSatisfyMinConfig(this.minimumCore)[core.CoreID]) {
				return true;
			} else {
				return false;
			}
		},
	},
	mounted() {
		const listableActiveCore = this.activeCore?.Deprecated ? undefined : this.activeCore;
		this.selectedCore = listableActiveCore ?? this.selectableCoresList[0];
	},
	computed: {
		...mapState(useCoresStore, {
			coresSatisfyMinConfig: "coresSatisfyMinConfig",
			correlationTrackingMethodByCode: "correlationTrackingMethodByCode",
		}),
		...mapState(useCoresStore, {
			filteredVisibleCoresList: "filteredVisibleCoresList",
			coresSatisfyMinConfig: "coresSatisfyMinConfig",
		}),
		selectableCoresList(): Core[] {
			return this.filteredVisibleCoresList.filter((core) => !core.Deprecated);
		},
		localShowDialog: {
			get(): boolean {
				return this.showDialog;
			},
			set(value: boolean) {
				this.$emit("update:showDialog", value);
			},
		},
		coreRequirementWarnings(): Partial<CoreRequirementMatch> {
			const requirementMatch = {};
			if (!this.selectedCore) {
				return requirementMatch;
			}
			for (const key in this.minimumCore) {
				if (typeof this.minimumCore[key] == "number") {
					// check if requirement satisfied for numerical constraints
					requirementMatch[key] = this.selectedCore[key] < this.minimumCore[key];
				} else {
					// check if requirement satisfied for non numerical constraints
					if (["Class", "Microarchitecture", "CorrelationTracking"].includes(key)) {
						requirementMatch[key] = this.selectedCore[key] !== this.minimumCore[key];
					}
				}
			}
			return requirementMatch;
		},
		coreDescription() {
			if (this.selectedCore && this.selectedCore.Owner == "*") {
				const description = coreDescriptionString(this.selectedCore.Name, false);
				return description;
			} else {
				return "";
			}
		},
	},
	created() {
		this.coresStore.getCoresFilteredCoresByUserTier();
	},
});
</script>

<style lang="scss">
.content-grid {
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: auto;
	grid-template-areas:
		"core-list"
		"grid-divider"
		"core-details";

	/* scroll region for the scrollable dialog (replaces v-card-text) */
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}
.content-grid-core-list {
	display: flex;
	flex-direction: row;
	grid-area: "core-list";
	flex: auto;

	/* gap between core cards */
	gap: var(--sgnl-space-8);
	padding: var(--sgnl-space-24);

	max-height: none;
	overflow-x: scroll;
	overflow-y: hidden;
}
.content-grid-core-details {
	grid-area: "core-details";
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-24);
}
.core-details-spec-group {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}
.core-details-spec-group .cell {
	min-width: 130px;
}
.content-grid-grid-divider {
	grid-area: "grid-divider";
}
.content-grid-error {
	grid-column: 1 / -1;
	padding: var(--sgnl-space-24);
}

@media (min-width: map-get($grid-breakpoints,md)) {
	.content-grid-core-list {
		max-height: 510px;
		flex-direction: column;
		overflow-x: hidden;
		overflow-y: auto;
	}

	.content-grid-core-details {
		max-height: 510px;
		overflow-y: auto;
	}

	.content-grid {
		grid-template-columns: 250px min-content auto;
		grid-template-rows: 1fr;
		grid-template-areas: "core-list grid-divider core-details";
		column-gap: var(--sgnl-space-2);
	}
}

.userflow-core-selector-dialog .core-warning-alert {
	flex: 0 0 auto;
}
</style>
